import { updateSchema } from '../../common/inputSchemas/document';
import { type AppRouter } from '../../server/appRouter';
import { buildAttributeSchema } from '../lib/buildAttributeSchema';
import { createSaveHandler } from '../lib/createSaveHandler';
import { trpc } from '../lib/trpc';
import { Form } from './Form';
import { zodResolver } from '@hookform/resolvers/zod';
import { type inferRouterOutputs } from '@trpc/server';
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

export const Singleton = ({
  params,
}: {
  readonly params: inferRouterOutputs<AppRouter>['models']['list'][number];
}) => {
  const { data: documentData } = trpc.documents.show.useQuery(
    {
      default: {},
      documentId: params.documents?.id || 'N/A',
    },
    { staleTime: Infinity, suspense: true },
  );
  const schema = z
    .object({
      attributes: buildAttributeSchema(params.models.schema.fields),
    })
    .merge(updateSchema.pick({ slug: true, title: true }));

  const formDefaultValues = useMemo(
    () => ({
      attributes: Object.fromEntries(
        documentData?.attributes?.map((attr) => [
          attr.fieldId,
          attr.textValue || '',
        ]) || [],
      ),
      slug: documentData?.slug || '',
      title: documentData?.title || '',
    }),
    [documentData?.attributes, documentData?.slug, documentData?.title],
  );

  const form = useForm({
    defaultValues: formDefaultValues,
    resolver: zodResolver(schema),
  });

  const { mutateAsync: updateSingletonAsync } =
    trpc.documents.updateSingleton.useMutation();

  const utils = trpc.useUtils();

  const submitHandler = createSaveHandler(
    form,
    async (data) =>
      updateSingletonAsync({
        data,
        modelId: params.models.id,
      }),

    async () => {
      utils.models.show.invalidate(params.models.id);
      if (params.documents?.id) {
        utils.documents.show.invalidate({
          default: {},
          documentId: params.documents.id,
        });
      }
    },
  );

  return (
    <FormProvider {...form}>
      <Form
        modelData={params.models}
        submitHandler={submitHandler}
        title={params.models.name}
      />
    </FormProvider>
  );
};
